import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

export default function About() {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isVisible = (id: string) => visibleSections.has(id);

  return (
    <div className="bg-cream min-h-screen">
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 lg:px-20">
          <div className="max-w-4xl">
            <h1 className="font-serif text-5xl md:text-7xl text-forest leading-[0.95] mb-8">
              Built on trust,<br />
              <span className="italic">designed for</span><br />
              community
            </h1>
            <p className="font-grotesk text-lg text-forest/60 max-w-xl leading-relaxed">
              Paw & Co. began with a simple observation: urban pet parents deserved better. 
              Not just better care, but a better experience.
            </p>
          </div>
        </section>

        {/* Story */}
        <section id="story" data-animate className={`px-6 md:px-12 lg:px-20 pb-24 md:pb-32 transition-all duration-700 ${isVisible('story') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <div className="aspect-[4/5] overflow-hidden border border-forest/10 mb-8">
                <img
                  src="https://static.readdy.ai/image/1f65bef57b9643672cbad222c3f4355a/004a7781a93d12bcea30aa97242de8b5.jpeg"
                  alt="Pet parent with dog"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
            <div className="lg:pt-12">
              <h2 className="font-serif text-3xl md:text-4xl text-forest mb-8">Our Story</h2>
              <div className="space-y-6">
                <p className="font-grotesk text-base text-forest/60 leading-relaxed">
                  In 1994, Paw & Co. began with a rebellious spirit. Our founder, a young girl with a love for animals and a need to fund her teenage adventures - beer and cigarettes included - started walking neighbors' dogs. What began as a simple hustle soon turned into a landslide of requests. People didn't just want a walker they wanted her raw passion.
                </p>
                <p className="font-grotesk text-base text-forest/60 leading-relaxed">
                  She quickly traded the solo hustle for a curated team of elite professionals. As the decades passed, we meticulously added grooming, training, and matchmaking to our repertoire. Every expansion was handled with the same bold energy that defined our first walk, ensuring that professional expertise never compromised our original, authentic soul.
                </p>
                <p className="font-grotesk text-base text-forest/60 leading-relaxed">
                  Today, Paw & Co. is a global collective. With trusted partners in nearly every major city, we bring high-end pet care to parents across the world. We have grown into an international network, but our core principle remains: your pet deserves care that is as bold, thoughtful, and genuine as it was back in the nineties.
                </p>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}